import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';

// Custom tooltip component for BCR charts
interface CustomTooltipProps {
    active?: boolean;
    payload?: any[];
    label?: number;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
    if (active && payload && payload.length > 0) {
        // Filter out entries that are null/undefined
        const validEntries = payload.filter(entry => entry.value != null);

        if (validEntries.length === 0) return null;

        return (
            <div
                style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    padding: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}>
                <p style={{ margin: '0 0 4px 0', fontWeight: 'bold' }}>{`H = ${Number(label).toFixed(2)} m`}</p>
                {validEntries.map((entry, index) => {
                    const fsValue = Number(entry.value);
                    // Check if this is an intersection point (integer FS value)
                    const isIntersection = Number.isInteger(fsValue) && fsValue >= 1 && fsValue <= 8;
                    const displayText = isIntersection ? `${entry.name}: FS = ${fsValue} (intersection)` : `${entry.name}: FS = ${fsValue.toFixed(2)}`;

                    return (
                        <p
                            key={index}
                            style={{
                                margin: '2px 0',
                                color: entry.color,
                                fontWeight: isIntersection ? 'bold' : 'normal',
                            }}>
                            {displayText}
                        </p>
                    );
                })}
            </div>
        );
    }
    return null;
};

// Definisci il tipo per i dati
interface DataPoint {
    x: number;
    y: number;
}

// Definisci il tipo per la legenda opzionale
export interface ChartLegendItem {
    dataKey: string; // Original key like 'unreinforced', 'reinforced', 'vertPressure'
    color: string;
    name: string;
}

export interface LineChartProps {
    data: DataPoint[]; // For non-static: unreinforced FS vs h(m). For static: stab kPa vs H(m)
    reinforcedData?: DataPoint[]; // For non-static: reinforced FS vs h(m). For static: unstab kPa vs H(m)
    bcrData?: DataPoint[]; // For non-static: bcr FS vs h(m). For static: vertPressure kPa vs H(m)
    xMax?: number; // Max h(m) for non-static, or Max q (kPa) for static
    yMax?: number; // Max FS for non-static, or Max H (m) for static
    xLabel?: string;
    yLabel?: string;
    legend?: ChartLegendItem[];
    method: 'static' | 'bcr'; // Determines if the chart is static or non-static
}

// const legend = [
//     { dataKey: 'reinforced', color: '#1b6f34', name: 'Reinforced' },
//     { dataKey: 'unreinforced', color: '#d32f2f', name: 'Unreinforced' },
//     { dataKey: 'vertPressure', color: '#1976d2', name: 'Vert. Pressure' }
// ];

const LineChartComponent: React.FC<LineChartProps> = ({ data, reinforcedData, bcrData, xMax, yMax, xLabel, yLabel, legend, method }) => {
    const isStaticChart = method === 'static';

    // Note: Intersection points are now calculated inline in the custom dot function

    // Data for the <LineChart> component itself - keep original curve data intact
    const chartData = React.useMemo(() => {
        if (isStaticChart) {
            // For static charts, use Y values as they come (already negative from backend)
            return data.map(point => ({
                x: point.x,
                y: point.y, // Use Y values as they are (negative from backend)
            }));
        } else {
            // For non-static charts, use original data to maintain curve continuity
            return data.map((point, index) => ({
                x: point.x,
                unreinforced: point.y,
                reinforced: reinforcedData ? reinforcedData[index]?.y : undefined,
            }));
        }
    }, [isStaticChart, data, reinforcedData]);

    // Calculate X domain maximum
    const currentXMax = React.useMemo(() => {
        if (isStaticChart) {
            const allXValues = [...data.map(p => p.x), ...(reinforcedData?.map(p => p.x) || []), ...(bcrData?.map(p => p.x) || [])].filter(x => typeof x === 'number');
            const maxX = allXValues.length > 0 ? Math.max(...allXValues) : 0;
            // For static chart, X-axis is q (kPa). Let's use a dynamic max or a sensible default.
            // Example: round up to nearest 50, min 200 for kPa, or use passed xMax if available.
            return xMax ?? Math.max(200, Math.ceil(maxX / 50) * 50);
        }
        return xMax; // Domain for h(m) (non-static)
    }, [isStaticChart, data, reinforcedData, bcrData, xMax]);

    // Calculate Y domain maximum and minimum for static charts
    const currentYDomain = React.useMemo((): [number, number | string] | [string, number | string] => {
        if (isStaticChart) {
            // For static chart, Y-axis is H (m) with negative values from backend
            // If yMax is explicitly passed (e.g., for parking_deck), use it.
            // Otherwise, default to 6 for other static charts.
            const maxH = yMax !== undefined ? yMax : 6;
            return [-maxH, 0]; // Negative domain: from -maxH to 0
        }
        return [1, yMax ?? 'auto']; // Domain for FS (non-static)
    }, [isStaticChart, yMax]);

    // Generate x-axis ticks
    const currentXTicks = React.useMemo(() => {
        if (isStaticChart && typeof currentXMax === 'number') {
            // X-axis is q (kPa) for static chart.
            const interval = currentXMax <= 100 ? 20 : currentXMax <= 500 ? 50 : 100;
            return Array.from({ length: Math.floor(currentXMax / interval) + 1 }, (_, i) => i * interval);
        }
        // Non-static chart (X-axis is h(m)
        return typeof xMax === 'number' ? Array.from({ length: Math.floor(xMax / 0.2) + 1 }, (_, i) => Number((i * 0.2).toFixed(2))) : undefined;
    }, [isStaticChart, currentXMax, xMax]);

    // Generate y-axis ticks
    const currentYTicks = React.useMemo(() => {
        if (isStaticChart) {
            // Y-axis is H (m) for static chart, negative values from -maxH to 0
            const maxH = yMax !== undefined ? yMax : 6;
            const interval = maxH <= 2 ? 0.5 : 0.5; // e.g., 0.5 for 0-2m, 0.5 for 0-6m
            // Generate negative ticks from -maxH to 0
            const numTicks = Math.floor(maxH / interval);
            return Array.from({ length: numTicks + 1 }, (_, i) => -parseFloat((i * interval).toFixed(1)));
        }
        // Non-static chart (Y-axis is FS) - show all numbers from 1 to yMax
        if (yMax && yMax >= 1) {
            return Array.from({ length: yMax }, (_, i) => i + 1);
        }
        return yMax ? Array.from({ length: Math.floor(yMax) + 1 }, (_, i) => i) : undefined;
    }, [isStaticChart, yMax]);

    const renderLines = () => {
        if (isStaticChart && legend) {
            return legend.map(item => {
                let lineData: DataPoint[] | undefined;
                // Determine the correct dataset based on the original dataKey/name from Results.tsx
                // The 'dataKey' in the legend item from Results.tsx helps us map to the correct prop.
                if (item.dataKey === 'reinforced') {
                    // Corresponds to STAB. BEARING CAPACITY qra
                    lineData = data.map(point => ({ x: point.x, y: point.y })); // Use Y as is (negative from backend)
                } else if (item.dataKey === 'unreinforced') {
                    // Corresponds to UNSTAB. BEARING CAPACITY qua
                    lineData = reinforcedData?.map(point => ({ x: point.x, y: point.y })); // Use Y as is (negative from backend)
                } else if (item.dataKey === 'vertPressure') {
                    // Corresponds to VERT. PRESSURE σv
                    lineData = bcrData?.map(point => ({ x: point.x, y: point.y })); // Use Y as is (negative from backend)
                }

                if (!lineData) return null;

                return (
                    <Line
                        key={item.name}
                        type="monotone"
                        data={lineData} // Assign the specific dataset to this line
                        dataKey="y" // Y-values (kPa) are in the 'y' property of DataPoint
                        stroke={item.color}
                        name={item.name}
                        dot={false}
                        strokeWidth={1}
                        isAnimationActive={false}
                        connectNulls={true}
                    />
                );
            });
        }

        // Default rendering for NON-STATIC charts
        return (
            <>
                <Line
                    type="monotone"
                    dataKey="unreinforced"
                    stroke="#0000ff"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 7, fill: '#0000ff', stroke: '#ffffff', strokeWidth: 2 }}
                    isAnimationActive={false}
                    connectNulls={true}
                />
                {reinforcedData && (
                    <Line
                        type="monotone"
                        dataKey="reinforced"
                        stroke="#ff0000"
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 7, fill: '#ff0000', stroke: '#ffffff', strokeWidth: 2 }}
                        isAnimationActive={false}
                        connectNulls={true}
                    />
                )}
            </>
        );
    };

    return (
        <div style={{ width: '100%', height: 400 }} id="line-chart-results">
            <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 20, right: 120, left: 20, bottom: 20 }}>
                    <CartesianGrid stroke="lightgrey" strokeDasharray="1 1" strokeOpacity={0.3} />
                    {/* Custom reference lines for static charts */}
                    {isStaticChart && (
                        <>
                            {/* Solid line at Y=0 */}
                            <ReferenceLine y={0} stroke="grey" strokeWidth={1} />
                            {/* Dashed line at Y=-6 */}
                            <ReferenceLine y={-6} stroke="grey" strokeWidth={1} strokeDasharray="5 5" />
                        </>
                    )}
                    <XAxis
                        dataKey="x"
                        label={{ value: xLabel ?? (isStaticChart ? 'q (kPa)' : 'h(m)'), position: isStaticChart ? 'top' : 'bottom', style: { fontSize: 18, fontFamily: 'Arial', fontWeight: 600 } }}
                        type="number"
                        domain={[0, currentXMax ?? 'dataMax']}
                        ticks={currentXTicks}
                        allowDataOverflow={false}
                        tick={{ fontSize: 12, fontFamily: 'Arial' }}
                        interval={isStaticChart ? undefined : 0}
                        tickCount={isStaticChart ? undefined : xMax ? Math.floor(xMax / 0.2) + 1 : undefined}
                        orientation={isStaticChart ? 'top' : 'bottom'}
                        axisLine={isStaticChart ? { stroke: 'grey', strokeWidth: 1 } : true}
                    />
                    <YAxis
                        label={{ value: yLabel ?? (isStaticChart ? 'H (m)' : 'FS'), angle: -90, position: 'left', style: { fontSize: 18, fontFamily: 'Arial', fontWeight: 600 } }}
                        type="number"
                        domain={currentYDomain}
                        ticks={currentYTicks}
                        allowDataOverflow={isStaticChart}
                        tick={{ fontSize: 12, fontFamily: 'Arial' }}
                        interval={isStaticChart ? undefined : 0}
                        tickCount={isStaticChart ? undefined : yMax ? Math.floor(yMax) + 1 : undefined}
                        tickFormatter={undefined}
                    />
                    <Tooltip content={method === 'bcr' ? <CustomTooltip /> : undefined} />
                    <Legend layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ paddingLeft: '10px', fontFamily: 'Arial' }} />

                    {renderLines()}
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

export default LineChartComponent;
